// Art assets with procedural fallback. See ART.md. The manifest maps ids to files under
// /art/. Nothing here blocks: images load in the background and drawArt returns false until
// an image is ready, so callers keep drawing procedurally.

export interface ManifestEntry { src: string; note?: string }
export type Manifest = Record<string, ManifestEntry>;

const images = new Map<string, HTMLImageElement>();
const ready = new Set<string>();
const failed = new Set<string>();
let manifest: Manifest = {};
let loaded = false;
const listeners = new Set<() => void>();

/** Load the manifest and start fetching every image. Safe to call once at boot. */
export async function preloadArt(base = '/art/'): Promise<void> {
  try {
    const res = await fetch(base + 'manifest.json', { cache: 'no-cache' });
    if (!res.ok) { loaded = true; return; }
    manifest = (await res.json()) as Manifest;
  } catch { loaded = true; return; }
  loaded = true;
  for (const [id, entry] of Object.entries(manifest)) {
    const img = new Image();
    img.decoding = 'async';
    img.onload = () => { ready.add(id); for (const l of listeners) l(); };
    img.onerror = () => { failed.add(id); };
    img.src = base + entry.src;
    images.set(id, img);
  }
}

/** called when any image finishes loading, so screens can repaint */
export function onArtReady(fn: () => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

export function hasArt(id: string): boolean { return ready.has(id); }
export function artLoaded(): boolean { return loaded; }

export function art(id: string): HTMLImageElement | null {
  return ready.has(id) ? images.get(id)! : null;
}

/** A cell in a sprite sheet: `count` columns, optional `rows` with a `row` index. */
export interface Frame { index: number; count: number; row?: number; rows?: number }

function cell(img: HTMLImageElement, f?: Frame): [number, number, number, number] {
  if (!f) return [0, 0, img.naturalWidth, img.naturalHeight];
  const fw = img.naturalWidth / f.count;
  const fh = img.naturalHeight / (f.rows ?? 1);
  return [fw * f.index, fh * (f.row ?? 0), fw, fh];
}

/** Draw an image if it is available. Returns false so the caller can fall back. */
export function drawArt(ctx: CanvasRenderingContext2D, id: string, x: number, y: number, w: number, h: number, frame?: Frame): boolean {
  const img = art(id);
  if (!img) return false;
  const [sx, sy, sw, sh] = cell(img, frame);
  ctx.drawImage(img, sx, sy, sw, sh, x, y, w, h);
  return true;
}

/**
 * Fit an image inside a box without stretching, bottom-centered (things stand on the floor).
 * `bleed` scales up a little because generated art keeps a generous empty margin.
 */
export function drawArtContain(ctx: CanvasRenderingContext2D, id: string, x: number, y: number, w: number, h: number, bleed = 1.25): boolean {
  const img = art(id);
  if (!img) return false;
  const s = Math.min(w / img.naturalWidth, h / img.naturalHeight) * bleed;
  const dw = img.naturalWidth * s, dh = img.naturalHeight * s;
  ctx.drawImage(img, x + (w - dw) / 2, y + h - dh + (dh - Math.min(dh, h * bleed)) * 0 + (dh > h ? (dh - h) * 0.6 : 0), dw, dh);
  return true;
}

const tintCanvas = document.createElement('canvas');

/**
 * Draw a white-with-gray-shading layer tinted with a color (see ART.md section 3).
 * Multiplies the color into the layer while keeping its alpha.
 */
export function drawTinted(ctx: CanvasRenderingContext2D, id: string, color: string, x: number, y: number, w: number, h: number, frame?: Frame): boolean {
  const img = art(id);
  if (!img) return false;
  const [sx, sy, sw, sh] = cell(img, frame);
  tintCanvas.width = sw; tintCanvas.height = sh;
  const t = tintCanvas.getContext('2d')!;
  t.clearRect(0, 0, sw, sh);
  t.drawImage(img, sx, sy, sw, sh, 0, 0, sw, sh);
  t.globalCompositeOperation = 'multiply';
  t.fillStyle = color;
  t.fillRect(0, 0, sw, sh);
  t.globalCompositeOperation = 'destination-in';
  t.drawImage(img, sx, sy, sw, sh, 0, 0, sw, sh);
  t.globalCompositeOperation = 'source-over';
  ctx.drawImage(tintCanvas, x, y, w, h);
  return true;
}

/** An <img> element for DOM use (cards, shop), or null to fall back. */
export function artImg(id: string, size: number): HTMLImageElement | null {
  const img = art(id);
  if (!img) return null;
  const el = new Image();
  el.src = img.src;
  el.width = size; el.height = size;
  el.style.width = size + 'px'; el.style.height = size + 'px';
  el.style.objectFit = 'contain';
  return el;
}

/** Fill a rect with a tiled pattern image if available. */
export function fillPattern(ctx: CanvasRenderingContext2D, id: string, x: number, y: number, w: number, h: number, scale = 0.5): boolean {
  const img = art(id);
  if (!img) return false;
  const pat = ctx.createPattern(img, 'repeat');
  if (!pat) return false;
  ctx.save();
  ctx.translate(x, y);
  const m = new DOMMatrix().scale(scale);
  (pat as any).setTransform?.(m);
  ctx.fillStyle = pat;
  ctx.fillRect(0, 0, w, h);
  ctx.restore();
  return true;
}
